/**
 * Analytics Hook
 *
 * Handles fetching and managing analytics data for dashboard displays.
 * Supports date range filtering and various metric types.
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { apiRequest, ApiError, resolveApiBaseUrl } from '../utils/apiClient';
import { useAuth } from './useAuth';

export type DateRange = '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';

export interface AnalyticsOverview {
  totalViews: number;
  totalClicks: number;
  totalContacts: number;
  totalSaves: number;
  conversionRate: number;
  viewsTrend: number;
  clicksTrend: number;
  contactsTrend: number;
}

export interface TimeSeriesData {
  date: string;
  views: number;
  clicks: number;
  contacts: number;
  saves: number;
}

export interface PropertyAnalytics {
  propertyId: string;
  propertyTitle: string;
  propertyImage?: string;
  views: number;
  clicks: number;
  contacts: number;
  saves: number;
  conversionRate: number;
}

export interface HeatmapData {
  hour: number;
  day: number;
  value: number;
}

export interface MarketComparison {
  metric: string;
  yourValue: number;
  marketAverage: number;
  percentile: number;
}

interface AnalyticsState {
  overview: AnalyticsOverview | null;
  timeSeries: TimeSeriesData[];
  propertyAnalytics: PropertyAnalytics[];
  heatmapData: HeatmapData[];
  marketComparison: MarketComparison[];
  dateRange: DateRange;
  customDateRange: { start: string; end: string } | null;
  loading: boolean;
  error: string | null;
}

interface AnalyticsContextType extends AnalyticsState {
  fetchOverview: () => Promise<void>;
  fetchTimeSeries: () => Promise<void>;
  fetchPropertyAnalytics: () => Promise<void>;
  fetchHeatmapData: () => Promise<void>;
  fetchMarketComparison: () => Promise<void>;
  fetchAll: () => Promise<void>;
  setDateRange: (range: DateRange, customRange?: { start: string; end: string }) => void;
  exportReport: (format: 'pdf' | 'excel' | 'csv') => Promise<Blob | null>;
}

const defaultOverview: AnalyticsOverview = {
  totalViews: 0,
  totalClicks: 0,
  totalContacts: 0,
  totalSaves: 0,
  conversionRate: 0,
  viewsTrend: 0,
  clicksTrend: 0,
  contactsTrend: 0,
};

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const token = session?.token;

  const [state, setState] = useState<AnalyticsState>({
    overview: null,
    timeSeries: [],
    propertyAnalytics: [],
    heatmapData: [],
    marketComparison: [],
    dateRange: '30d',
    customDateRange: null,
    loading: false,
    error: null,
  });

  const getDateParams = useCallback(() => {
    if (state.dateRange === 'custom' && state.customDateRange) {
      return `startDate=${state.customDateRange.start}&endDate=${state.customDateRange.end}`;
    }
    return `range=${state.dateRange}`;
  }, [state.dateRange, state.customDateRange]);

  const setDateRange = useCallback((range: DateRange, customRange?: { start: string; end: string }) => {
    setState(prev => ({
      ...prev,
      dateRange: range,
      customDateRange: range === 'custom' ? customRange || null : null,
    }));
  }, []);

  const fetchOverview = useCallback(async () => {
    if (!token) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiRequest<{ overview: AnalyticsOverview }>(
        `/analytics/overview?${getDateParams()}`,
        {
          method: 'GET',
          token,
        }
      );

      setState(prev => ({
        ...prev,
        overview: response.overview || defaultOverview,
        loading: false,
      }));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load analytics overview';
      setState(prev => ({ ...prev, error: message, loading: false }));
    }
  }, [token, getDateParams]);

  const fetchTimeSeries = useCallback(async () => {
    if (!token) return;

    try {
      const response = await apiRequest<{ data: TimeSeriesData[] }>(
        `/analytics/time-series?${getDateParams()}`,
        {
          method: 'GET',
          token,
        }
      );

      setState(prev => ({
        ...prev,
        timeSeries: response.data || [],
      }));
    } catch (err) {
      console.warn('Failed to fetch time series data:', err);
    }
  }, [token, getDateParams]);

  const fetchPropertyAnalytics = useCallback(async () => {
    if (!token) return;

    try {
      const response = await apiRequest<{ properties: PropertyAnalytics[] }>(
        `/analytics/properties?${getDateParams()}`,
        {
          method: 'GET',
          token,
        }
      );

      setState(prev => ({
        ...prev,
        propertyAnalytics: response.properties || [],
      }));
    } catch (err) {
      console.warn('Failed to fetch property analytics:', err);
    }
  }, [token, getDateParams]);

  const fetchHeatmapData = useCallback(async () => {
    if (!token) return;

    try {
      const response = await apiRequest<{ data: HeatmapData[] }>(
        `/analytics/heatmap?${getDateParams()}`,
        {
          method: 'GET',
          token,
        }
      );

      setState(prev => ({
        ...prev,
        heatmapData: response.data || [],
      }));
    } catch (err) {
      console.warn('Failed to fetch heatmap data:', err);
    }
  }, [token, getDateParams]);

  const fetchMarketComparison = useCallback(async () => {
    if (!token) return;

    try {
      const response = await apiRequest<{ comparison: MarketComparison[] }>(
        `/analytics/market-comparison?${getDateParams()}`,
        {
          method: 'GET',
          token,
        }
      );

      setState(prev => ({
        ...prev,
        marketComparison: response.comparison || [],
      }));
    } catch (err) {
      console.warn('Failed to fetch market comparison:', err);
    }
  }, [token, getDateParams]);

  const fetchAll = useCallback(async () => {
    if (!token) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      await Promise.all([
        fetchOverview(),
        fetchTimeSeries(),
        fetchPropertyAnalytics(),
        fetchHeatmapData(),
        fetchMarketComparison(),
      ]);
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [token, fetchOverview, fetchTimeSeries, fetchPropertyAnalytics, fetchHeatmapData, fetchMarketComparison]);

  const exportReport = useCallback(async (format: 'pdf' | 'excel' | 'csv'): Promise<Blob | null> => {
    if (!token) return null;

    try {
      const response = await fetch(
        `${resolveApiBaseUrl()}/analytics/export?${getDateParams()}&format=${format}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export report');
      }

      return await response.blob();
    } catch (err) {
      console.warn('Failed to export report:', err);
      return null;
    }
  }, [token, getDateParams]);

  const value = useMemo<AnalyticsContextType>(
    () => ({
      ...state,
      fetchOverview,
      fetchTimeSeries,
      fetchPropertyAnalytics,
      fetchHeatmapData,
      fetchMarketComparison,
      fetchAll,
      setDateRange,
      exportReport,
    }),
    [
      state,
      fetchOverview,
      fetchTimeSeries,
      fetchPropertyAnalytics,
      fetchHeatmapData,
      fetchMarketComparison,
      fetchAll,
      setDateRange,
      exportReport,
    ]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics(): AnalyticsContextType {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}

// Helper function to format large numbers
export function formatMetricValue(value: number, locale: string = 'en-US'): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString(locale);
}

// Helper to calculate percentage change
export function calculateTrend(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

// Helper to get date range label
export function getDateRangeLabel(range: DateRange, locale: string = 'en'): string {
  const labels: Record<DateRange, { en: string; ar: string }> = {
    '7d': { en: 'Last 7 days', ar: 'آخر 7 أيام' },
    '30d': { en: 'Last 30 days', ar: 'آخر 30 يوم' },
    '90d': { en: 'Last 90 days', ar: 'آخر 90 يوم' },
    '1y': { en: 'Last year', ar: 'آخر سنة' },
    'all': { en: 'All time', ar: 'كل الوقت' },
    'custom': { en: 'Custom range', ar: 'نطاق مخصص' },
  };
  return labels[range][locale === 'ar' ? 'ar' : 'en'];
}
